import React, { useState, forwardRef } from "react";
import { Eye, EyeSlash } from "@phosphor-icons/react";

/**
 * PasswordInput — text input with a show/hide-password toggle.
 *
 * Drop-in replacement for a native `<input type="password" />`. Accepts all
 * standard input props (value, onChange, autoComplete, required, disabled,
 * placeholder, data-testid, etc.) and forwards refs so form libraries and
 * focus() calls still work.
 *
 * The toggle button is positioned inside the input (right side). We pad the
 * right side of the input so the eye icon never covers typed text.
 */
const PasswordInput = forwardRef(function PasswordInput(
    { className = "", showToggleTestId, ...rest },
    ref,
) {
    const [visible, setVisible] = useState(false);
    // Preserve caller-provided data-testid on the input; derive the toggle's
    // testid from it (or `showToggleTestId`) so tests can target it too.
    const inputTestId = rest["data-testid"];
    const toggleTestId =
        showToggleTestId ||
        (inputTestId ? `${inputTestId}-toggle` : "password-toggle-btn");

    return (
        <div className="relative">
            <input
                ref={ref}
                {...rest}
                type={visible ? "text" : "password"}
                className={`${className} pr-11`}
            />
            <button
                type="button"
                tabIndex={-1}
                onClick={() => setVisible((v) => !v)}
                aria-label={visible ? "Hide password" : "Show password"}
                title={visible ? "Hide password" : "Show password"}
                data-testid={toggleTestId}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-[#525252] hover:text-[#0A0A0A] transition-colors"
            >
                {visible ? (
                    <EyeSlash size={18} weight="regular" />
                ) : (
                    <Eye size={18} weight="regular" />
                )}
            </button>
        </div>
    );
});

export default PasswordInput;
